import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, TrendingUp, LineChart, CalendarRange, DollarSign, ArrowUpCircle, ArrowDownCircle, Wallet, Calendar, ChevronRight, BanknoteIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Booking } from '../lib/bookings';
import { Payment } from '../lib/payments';

const dateRanges = [
  { id: 'this-month', name: 'This Month' },
  { id: 'last-month', name: 'Last Month' },
  { id: 'next-month', name: 'Next Month' },
  { id: 'custom', name: 'Custom Range', icon: CalendarRange }
];

// Mock data for metrics
const mockMetrics = [
  {
    id: 'total-revenue',
    name: 'Total Sales After Discounts',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: DollarSign
  },
  {
    id: 'total-costs',
    name: 'Service Costs',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: Wallet
  },
  {
    id: 'net-profit',
    name: 'Gross Profit',
    value: '$0.00',
    change: '+0.0%',
    trend: 'up',
    icon: TrendingUp
  }
];

interface MaterialData {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
  total_cost: number;
  material_date: string;
}

interface LaborData {
  id: string;
  resource_name: string;
  hours: number;
  rate: number;
  total_cost: number;
  booking_id: string;
}

interface CategoryTotal {
  category: string;
  total_cost: number;
  materials: MaterialData[];
  isExpanded: boolean;
}

interface ResourceTotal {
  resource_id: string;
  first_name: string;
  last_name: string;
  total_earnings: number;
}

interface Business {
  id: string;
  name: string;
}

interface ProfitByJobBooking {
  id: string;
  booking_number: string;
  title: string;
  subtotal: number;
  discount_amount: number;
  material_costs: number;
  labour_costs: number;
  completed_at: string;
  status: string;
}

const reportPages = [
  { id: 'sales', name: 'Sales', icon: TrendingUp, path: '/sales-reports/sales' },
  { id: 'profit-loss', name: 'Profit & Loss', icon: LineChart, path: '/sales-reports/profit-loss' },
  { id: 'cash-flow', name: 'Cash Flow', icon: Wallet, path: '/sales-reports/cash-flow' }
];

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const getDateRangeText = (rangeId: string, startDate?: string, endDate?: string): string => {
  // Helper to create a UTC date that represents local midnight
  const createLocalDate = (date: Date | string) => {
    if (typeof date === 'string') {
      // For custom range dates, which come in as YYYY-MM-DD
      return new Date(date + 'T00:00:00Z');
    }
    // For other dates, ensure we're working with UTC midnight
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ));
  };

  const now = new Date();
  const utcNow = createLocalDate(now);

  switch (rangeId) {
    case 'this-month': {
      const monthName = now.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      return monthName;
    }
    case 'last-month': {
      const lastMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      return lastMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'next-month': {
      const nextMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      return nextMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'custom': {
      if (startDate && endDate) {
        const start = createLocalDate(startDate);
        const end = createLocalDate(endDate);
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
      return 'Custom Range';
    }
    default:
      return 'Select Date Range';
  }
};

// Function to check if a date falls within the selected range
const isDateInRange = (date: string | null | undefined, rangeId: string, customStartDate?: string, customEndDate?: string): boolean => {
  if (!date) return false;
  
  // Convert the booking end date to UTC midnight
  const targetDate = new Date(date + 'T00:00:00Z');
  const now = new Date();
  const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  switch (rangeId) {
    case 'this-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'last-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'next-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'custom': {
      if (customStartDate && customEndDate) {
        // Convert custom dates to UTC midnight
        const start = new Date(customStartDate + 'T00:00:00Z');
        const end = new Date(customEndDate + 'T23:59:59.999Z');
        return targetDate >= start && targetDate <= end;
      }
      return false;
    }
    default:
      return false;
  }
};

interface Resource {
  id: string;
  first_name: string;
  last_name: string;
}

interface PayrollEntry {
  id: string;
  resource_id: string;
  total_earnings: number;
  completed_at?: string;
  clock_out?: string;
  resource: {
    first_name: string;
    last_name: string;
  };
}

interface PayrollResponse {
  id: string;
  resource_id: string;
  total_earnings: number;
  completed_at?: string;
  clock_out?: string;
  resource: {
    first_name: string;
    last_name: string;
  };
}

export default function ProfitLoss() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(dateRanges[0]);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [materialUsageData, setMaterialUsageData] = useState<CategoryTotal[]>([]);
  const [laborData, setLaborData] = useState<ResourceTotal[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(true);
  const [isLoadingLabor, setIsLoadingLabor] = useState(true);
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    salesChange: 0,
    totalAfterDiscounts: 0,
    afterDiscountsChange: 0,
    totalOrders: 0,
    orderChange: 0,
    bookings: [] as Booking[]
  });
  const [profitByJobData, setProfitByJobData] = useState<ProfitByJobBooking[]>([]);
  const [paymentData, setPaymentData] = useState({
    totalPaymentsIn: 0,
    totalPaymentsOut: 0,
    paymentsInChange: 0,
    paymentsOutChange: 0
  });
  const [stockData, setStockData] = useState({
    totalSuppliesCost: 0,
    suppliesChange: 0
  });
  const [fixedExpenses, setFixedExpenses] = useState({
    totalFixedCost: 0,
    fixedCostChange: 0
  });
  const [useStockHistory, setUseStockHistory] = useState(() => {
    // Initialize from localStorage, default to false if not set
    const saved = localStorage.getItem('useStockHistory');
    return saved ? JSON.parse(saved) : false;
  });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();

  // Get current page name based on path
  const getCurrentPageName = () => {
    const currentPage = reportPages.find(page => page.path === location.pathname) || reportPages[0];
    return currentPage.name;
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setIsDateDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePageSelect = (path: string) => {
    navigate(path);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    fetchMaterialUsage();
    fetchLaborData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  const fetchMaterialUsage = async () => {
    if (!supabase || !selectedBusiness?.id) return;
    
    setIsLoadingMaterials(true);
    try {
      // Get date range based on selected filter
      let startDate: string | null = null;
      let endDate: string | null = null;

      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      switch (selectedDateRange.id) {
        case 'this-month':
          startDate = firstDayOfMonth.toISOString().split('T')[0];
          endDate = lastDayOfMonth.toISOString().split('T')[0];
          break;
        case 'last-month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
          endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
          break;
        case 'next-month':
          startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
          endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];
          break;
        case 'custom':
          startDate = customStartDate;
          endDate = customEndDate;
          break;
      }

      console.log('Fetching materials with date range:', { startDate, endDate });

      const { data: rawData, error: materialsError } = await supabase
        .from('booking_materials')
        .select(`
          id,
          quantity,
          cost,
          total_cost,
          created_at,
          material:material_id (
            id,
            name,
            description,
            unit,
            category:category_id (
              id,
              name
            )
          ),
          booking:booking_id (
            id,
            status,
            end_date,
            business_id
          )
        `)
        .eq('booking.business_id', selectedBusiness.id)
        .eq('booking.status', 'completed')
        .gte('booking.end_date', startDate)
        .lte('booking.end_date', endDate);

      if (materialsError) {
        console.error('Error fetching materials:', materialsError);
        throw materialsError;
      }

      console.log('Raw materials data:', rawData);

      if (!rawData?.length) {
        console.log('No materials data found');
        setMaterialUsageData([]);
        return;
      }

      // Process and group the data by category
      const groupedData = rawData.reduce((acc: { [key: string]: { [key: string]: MaterialData } }, item: any) => {
        // Only include materials from completed bookings with end_date in range
        if (item.booking?.status !== 'completed' || 
            !item.booking?.end_date || 
            !isDateInRange(item.booking.end_date, selectedDateRange.id, customStartDate, customEndDate)) {
          return acc;
        }
        
        const category = item.material?.category?.name || 'Uncategorized';
        const materialId = item.material?.id || 'unknown';
        
        if (!acc[category]) {
          acc[category] = {};
        }
        
        if (!acc[category][materialId]) {
          acc[category][materialId] = {
            id: materialId,
            name: item.material?.name || 'Unknown Material',
            category,
            quantity: 0,
            unit: item.material?.unit || 'unit',
            cost_per_unit: item.cost,
            total_cost: 0,
            material_date: item.booking.end_date
          };
        }

        // Sum up quantities and costs
        acc[category][materialId].quantity += item.quantity;
        acc[category][materialId].total_cost += item.total_cost;
        
        return acc;
      }, {});

      // Convert to array format with totals
      const categoryTotals: CategoryTotal[] = Object.entries(groupedData).map(([category, materials]) => ({
        category,
        total_cost: Object.values(materials).reduce((sum, mat) => sum + mat.total_cost, 0),
        materials: Object.values(materials).sort((a, b) => b.total_cost - a.total_cost),
        isExpanded: false,
      }));

      // Sort by total cost descending
      categoryTotals.sort((a, b) => b.total_cost - a.total_cost);
      
      console.log('Processed material data:', categoryTotals);
      setMaterialUsageData(categoryTotals);
    } catch (error) {
      console.error('Error fetching material usage:', error);
      setMaterialUsageData([]);
    } finally {
      setIsLoadingMaterials(false);
    }
  };

  const fetchLaborData = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setIsLoadingLabor(true);
      
      // Get date range
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      let start: Date;
      let end: Date;

      switch (selectedDateRange.id) {
        case 'this-month':
          start = firstDayOfMonth;
          end = lastDayOfMonth;
          break;
        case 'last-month':
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 'next-month':
          start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
          break;
        case 'custom':
          start = customStartDate ? new Date(customStartDate) : firstDayOfMonth;
          end = customEndDate ? new Date(customEndDate) : lastDayOfMonth;
          break;
        default:
          start = firstDayOfMonth;
          end = lastDayOfMonth;
      }

      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', start.toISOString())
        .lte('completed_at', end.toISOString());

      if (flatRateError) throw flatRateError;

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', start.toISOString())
        .lte('clock_out', end.toISOString());

      if (timeclockError) throw timeclockError;

      // Combine and group data by resource
      const combinedData = [
        ...(flatRateData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.total_earnings,
          completed_at: entry.completed_at,
          resource: {
            first_name: entry.resource?.first_name || '',
            last_name: entry.resource?.last_name || ''
          }
        })) || []),
        ...(timeclockData?.map(entry => ({
          id: entry.id,
          resource_id: entry.resource_id,
          total_earnings: entry.total_earnings,
          clock_out: entry.clock_out,
          resource: {
            first_name: entry.resource?.first_name || '',
            last_name: entry.resource?.last_name || ''
          }
        })) || [])
      ] as PayrollEntry[];

      const resourceTotals = combinedData.reduce((acc, entry) => {
        const resourceId = entry.resource_id;
        if (!acc[resourceId]) {
          acc[resourceId] = {
            resource_id: resourceId,
            first_name: entry.resource.first_name,
            last_name: entry.resource.last_name,
            total_earnings: 0
          };
        }
        acc[resourceId].total_earnings += entry.total_earnings || 0;
        return acc;
      }, {} as Record<string, ResourceTotal>);

      setLaborData(Object.values(resourceTotals));
    } catch (error) {
      console.error('Error fetching labor data:', error);
    } finally {
      setIsLoadingLabor(false);
    }
  };

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        if (!selectedBusiness?.id) {
          return; // Exit if no business is selected
        }

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch all bookings with end dates and include discounts and services with category details
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            *,
            customer:customers(
              id,
              created_at
            ),
            discounts:booking_discounts(*),
            services:booking_services_with_details(
              *,
              service_category_name
            )
          `)
          .eq('business_id', selectedBusiness.id)
          .order('end_date', { ascending: false });

        if (error) throw error;

        // Filter bookings for current period based on end_date and status
        const currentPeriodBookings = (bookings as Booking[]).filter(booking => 
          isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
          booking.status === 'completed'
        );

        // Filter bookings for previous period based on end_date and status
        const previousPeriodBookings = (bookings as Booking[]).filter(booking => {
          if (!booking.end_date || booking.status !== 'completed') return false;
          const bookingDate = new Date(booking.end_date + 'T00:00:00Z');
          return bookingDate >= previousPeriodStart && bookingDate < currentPeriodStart;
        });

        // Calculate metrics for current period
        const currentTotalSales = currentPeriodBookings.reduce((sum, booking) => sum + booking.subtotal, 0);
        const currentTotalAfterDiscounts = currentPeriodBookings.reduce((sum, booking) => {
          const subtotal = booking.subtotal;
          const discountAmount = booking.discount_amount || 0;
          return sum + (subtotal - discountAmount);
        }, 0);

        // Calculate metrics for previous period
        const previousTotalSales = previousPeriodBookings.reduce((sum, booking) => sum + booking.subtotal, 0);
        const previousTotalAfterDiscounts = previousPeriodBookings.reduce((sum, booking) => {
          const subtotal = booking.subtotal;
          const discountAmount = booking.discount_amount || 0;
          return sum + (subtotal - discountAmount);
        }, 0);

        // Calculate percentage changes
        const salesChange = previousTotalSales === 0 
          ? 100 
          : ((currentTotalSales - previousTotalSales) / previousTotalSales) * 100;

        const afterDiscountsChange = previousTotalAfterDiscounts === 0
          ? 100
          : ((currentTotalAfterDiscounts - previousTotalAfterDiscounts) / previousTotalAfterDiscounts) * 100;

        const orderChange = previousPeriodBookings.length === 0
          ? 100
          : ((currentPeriodBookings.length - previousPeriodBookings.length) / previousPeriodBookings.length) * 100;

        // Update sales data
        setSalesData({
          totalSales: currentTotalSales,
          salesChange: Number(salesChange.toFixed(1)),
          totalAfterDiscounts: currentTotalAfterDiscounts,
          afterDiscountsChange: Number(afterDiscountsChange.toFixed(1)),
          totalOrders: currentPeriodBookings.length,
          orderChange: Number(orderChange.toFixed(1)),
          bookings: currentPeriodBookings
        });

      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };

    fetchSalesData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Update payment data to match sales data
        setPaymentData({
          totalPaymentsIn: salesData.totalAfterDiscounts,
          totalPaymentsOut: laborData.reduce((sum, resource) => sum + resource.total_earnings, 0),
          paymentsInChange: salesData.afterDiscountsChange,
          paymentsOutChange: 0 // We'll calculate this later when we have historical data
        });

      } catch (error) {
        console.error('Error updating payment data:', error);
      }
    };

    fetchPaymentData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate, salesData, laborData]);

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch stock history for received items
        const { data: stockHistory, error } = await supabase
          .from('stock_history_with_details')
          .select('*')
          .eq('transaction_type', 'Received')
          .gte('created_at', currentPeriodStart.toISOString())
          .lte('created_at', selectedDateRange.id === 'custom' && customEndDate 
            ? new Date(customEndDate + 'T23:59:59.999Z').toISOString()
            : new Date(currentPeriodStart.getFullYear(), currentPeriodStart.getMonth() + 1, 0, 23, 59, 59, 999).toISOString()
          );

        if (error) {
          console.error('Error fetching stock data:', error);
          throw error;
        }

        console.log('Raw stock history:', stockHistory);

        // Calculate total cost for current period
        const currentTotalCost = (stockHistory || []).reduce((sum, item) => {
          const quantity = Math.abs(Number(item.quantity_change) || 0);
          const price = Number(item.price) || 0;
          const itemTotal = quantity * price;
          
          console.log('Item calculation:', { 
            name: item.item_name,
            category: item.category_name,
            quantity,
            price,
            itemTotal,
            date: item.created_at
          });
          
          return sum + itemTotal;
        }, 0);

        console.log('Current period total cost:', currentTotalCost);

        // Calculate previous period costs
        const { data: previousStockHistory, error: previousError } = await supabase
          .from('stock_history_with_details')
          .select('*')
          .eq('transaction_type', 'Received')
          .gte('created_at', previousPeriodStart.toISOString())
          .lt('created_at', currentPeriodStart.toISOString());

        if (previousError) {
          console.error('Error fetching previous stock data:', previousError);
          throw previousError;
        }

        const previousTotalCost = (previousStockHistory || []).reduce((sum, item) => {
          const quantity = Math.abs(Number(item.quantity_change) || 0);
          const price = Number(item.price) || 0;
          return sum + (quantity * price);
        }, 0);

        // Calculate percentage change
        const suppliesChange = previousTotalCost === 0 
          ? 100 
          : ((currentTotalCost - previousTotalCost) / previousTotalCost) * 100;

        setStockData({
          totalSuppliesCost: currentTotalCost,
          suppliesChange: Number(suppliesChange.toFixed(1))
        });

      } catch (error) {
        console.error('Error fetching stock data:', error);
      }
    };

    fetchStockData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const fetchFixedExpenses = async () => {
      try {
        if (!supabase || !selectedBusiness?.id) return;

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() + 1);
            previousPeriodStart.setDate(1);
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch operating costs for current period
        const { data: currentExpenses, error: currentError } = await supabase
          .from('operating_costs')
          .select('*')
          .eq('business_id', selectedBusiness.id);

        if (currentError) {
          console.error('Error fetching current operating costs:', currentError);
          throw currentError;
        }

        console.log('Current operating costs:', currentExpenses);

        // Calculate current period total
        const currentTotal = (currentExpenses || []).reduce((sum, expense) => {
          return sum + (Number(expense.amount) || 0);
        }, 0);

        // For fixed expenses, we don't need to fetch previous period since they're fixed
        // We'll set the change percentage to 0 since they should be constant
        setFixedExpenses({
          totalFixedCost: currentTotal,
          fixedCostChange: 0
        });

      } catch (error) {
        console.error('Error fetching operating costs:', error);
      }
    };

    fetchFixedExpenses();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  const handleStockHistoryToggle = (newValue: boolean) => {
    setUseStockHistory(newValue);
    localStorage.setItem('useStockHistory', JSON.stringify(newValue));
  };

  // Add new function to fetch profit by job data
  const fetchProfitByJobData = async () => {
    if (!supabase || !selectedBusiness?.id) return;

    try {
      console.log('Fetching profit by job data for business:', selectedBusiness.id);
      
      // Get bookings with their costs
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_number,
          title,
          subtotal,
          discount_amount,
          end_date,
          status,
          labour_costs,
          materials:booking_materials (
            quantity,
            cost
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'completed');

      if (bookingsError) {
        console.error('Error fetching bookings:', bookingsError);
        throw bookingsError;
      }

      // Transform the data
      const transformedData = (bookings || []).map(booking => {
        // Calculate material costs
        const materialCosts = (booking.materials || []).reduce((sum, material) => {
          const quantity = material.quantity || 0;
          const cost = material.cost || 0;
          return sum + (quantity * cost);
        }, 0);

        console.log('Booking costs:', {
          id: booking.id,
          materialCosts,
          labourCosts: booking.labour_costs || 0,
          totalCosts: materialCosts + (booking.labour_costs || 0)
        });

        return {
          id: booking.id,
          booking_number: booking.booking_number,
          title: booking.title,
          subtotal: booking.subtotal || 0,
          discount_amount: booking.discount_amount || 0,
          material_costs: materialCosts,
          labour_costs: booking.labour_costs || 0,
          completed_at: booking.end_date,
          status: booking.status
        };
      });

      console.log('Final transformed data:', transformedData);
      setProfitByJobData(transformedData);
    } catch (error) {
      console.error('Error fetching profit by job data:', error);
      setProfitByJobData([]);
    }
  };

  // Add useEffect to call the new function
  useEffect(() => {
    fetchProfitByJobData();
  }, [selectedBusiness?.id, selectedDateRange, customStartDate, customEndDate]);

  // Update metrics with real data
  const metrics = [
    {
      id: 'total-revenue',
      name: 'Total Sales After Discounts',
      value: `$${salesData.totalAfterDiscounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: `${salesData.afterDiscountsChange >= 0 ? '+' : ''}${salesData.afterDiscountsChange}%`,
      trend: salesData.afterDiscountsChange >= 0 ? 'up' : 'down',
      icon: DollarSign
    },
    {
      id: 'total-costs',
      name: 'Service Costs',
      value: `$${(materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `,
      valueExtra: `materials $${materialUsageData.reduce((sum, category) => sum + category.total_cost, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nlabor $${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: '+0.0%',
      trend: 'up',
      icon: Wallet
    },
    {
      id: 'net-profit',
      name: 'Gross Profit',
      value: `$${(salesData.totalAfterDiscounts - (materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `,
      valueExtra: `margin ${((salesData.totalAfterDiscounts - (materialUsageData.reduce((sum, category) => sum + category.total_cost, 0) + laborData.reduce((sum, resource) => sum + resource.total_earnings, 0))) / (salesData.totalAfterDiscounts || 1) * 100).toFixed(1)}%`,
      change: '+0.0%',
      trend: 'up',
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 overflow-x-hidden">
        <div className="flex flex-col h-full w-full overflow-x-hidden">
          {/* Navigation Bar */}
          <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 w-full">
            <div className="px-1 sm:px-3 md:px-6 py-1 sm:py-2 md:py-3 max-w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 md:gap-4">
                {/* Page Selector */}
                <div className="relative w-full sm:w-auto" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`
                      w-full sm:w-auto inline-flex items-center justify-between gap-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 
                      text-sm font-medium text-gray-700 
                      bg-white shadow-sm 
                      border border-gray-200 rounded-lg
                      transition-all duration-200 ease-in-out
                      hover:bg-gray-50 hover:border-gray-300
                      focus:outline-none focus:ring-2 focus:ring-blue-500/20
                      ${isDropdownOpen ? 'bg-gray-50 border-gray-300' : ''}
                    `}
                  >
                    <span className="text-gray-900">{getCurrentPageName()}</span>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                      aria-hidden="true"
                    />
                  </button>

                  {/* Page Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="fixed inset-x-0 top-auto sm:absolute sm:inset-auto sm:left-0 sm:right-auto sm:w-52 mt-1 bg-white rounded-lg shadow-lg border border-gray-200/50 backdrop-blur-sm z-[100] overflow-hidden mx-2 sm:mx-0">
                      <div className="py-1">
                        {reportPages.map((page) => {
                          const Icon = page.icon;
                          const isActive = page.path === location.pathname;
                          return (
                            <button
                              key={page.id}
                              onClick={() => handlePageSelect(page.path)}
                              className={`
                                w-full flex items-center px-3 py-2.5 text-sm
                                transition-colors duration-150 ease-in-out
                                ${isActive 
                                  ? 'bg-blue-50/80 text-blue-600' 
                                  : 'text-gray-700 hover:bg-gray-50'
                                }
                              `}
                            >
                              <Icon className={`h-4 w-4 mr-3 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                              {page.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Date Selector */}
                <div className="relative w-full sm:w-auto" ref={dateDropdownRef}>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <button
                      type="button"
                      onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                      className={`
                        flex-1 sm:flex-none inline-flex items-center justify-between gap-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 
                        text-sm font-medium text-gray-700 
                        bg-white shadow-sm 
                        border border-gray-200 rounded-lg
                        transition-all duration-200 ease-in-out
                        hover:bg-gray-50 hover:border-gray-300
                        focus:outline-none focus:ring-2 focus:ring-blue-500/20
                        ${isDateDropdownOpen ? 'bg-gray-50 border-gray-300' : ''}
                      `}
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Calendar className="h-4 w-4 flex-shrink-0 text-gray-500" />
                        <span className="text-gray-900 truncate">{selectedDateRange.name}</span>
                      </div>
                      <ChevronDown 
                        className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isDateDropdownOpen ? 'rotate-180' : ''}`} 
                        aria-hidden="true"
                      />
                    </button>
                    <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium truncate">
                      {getDateRangeText(selectedDateRange.id, customStartDate, customEndDate)}
                    </div>
                  </div>

                  {/* Date Dropdown Menu */}
                  {isDateDropdownOpen && (
                    <div className="fixed inset-x-0 top-auto sm:absolute sm:inset-auto sm:left-0 sm:right-auto sm:w-64 mt-1 bg-white rounded-lg shadow-lg border border-gray-200/50 backdrop-blur-sm z-[100] overflow-hidden mx-2 sm:mx-0">
                      <div className="py-1">
                        {dateRanges.map((dateRange) => {
                          const isActive = dateRange.id === selectedDateRange.id;
                          return (
                            <button
                              key={dateRange.id}
                              onClick={() => {
                                setSelectedDateRange(dateRange);
                                setIsDateDropdownOpen(false);
                                if (dateRange.id === 'custom') {
                                  setIsCustomRangeOpen(true);
                                }
                              }}
                              className={`
                                w-full flex items-center px-3 py-2.5 text-sm
                                transition-colors duration-150 ease-in-out
                                ${isActive 
                                  ? 'bg-blue-50/80 text-blue-600' 
                                  : 'text-gray-700 hover:bg-gray-50'
                                }
                              `}
                            >
                              {dateRange.icon && <dateRange.icon className={`h-4 w-4 mr-3 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />}
                              {dateRange.name}
                            </button>
                          );
                        })}
                      </div>

                      {/* Custom Range Selector */}
                      {isCustomRangeOpen && (
                        <div className="border-t border-gray-200 p-3">
                          <div className="space-y-3">
                            <div>
                              <label htmlFor="start-date" className="block text-xs font-medium text-gray-700 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                id="start-date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>
                            <div>
                              <label htmlFor="end-date" className="block text-xs font-medium text-gray-700 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                id="end-date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>
                            <button
                              onClick={() => {
                                setIsDateDropdownOpen(false);
                                setIsCustomRangeOpen(false);
                              }}
                              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            >
                              Apply Range
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-x-hidden">
            <div className="w-full max-w-full px-1 sm:px-3 md:px-6 py-1 sm:py-3 md:py-4 space-y-2 sm:space-y-6 overflow-x-hidden">
              {/* Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-2 md:gap-6">
                {metrics.map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <div
                      key={metric.id}
                      className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-2 sm:p-3 md:p-6 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <div className="p-1 sm:p-1.5 bg-blue-50 rounded-lg">
                            <Icon className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-blue-600" />
                          </div>
                          <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 line-clamp-1">{metric.name}</span>
                        </div>
                        <div className={`flex items-center ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                          {metric.trend === 'up' ? (
                            <ArrowUpCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
                          ) : (
                            <ArrowDownCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
                          )}
                          <span className="text-[10px] sm:text-xs font-medium ml-0.5 sm:ml-1">{metric.change}</span>
                        </div>
                      </div>
                      <div className="mt-1.5 sm:mt-2 md:mt-3">
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm sm:text-base md:text-2xl font-semibold text-gray-900">{metric.value}</span>
                          {metric.valueExtra && (
                            <div className="text-xs sm:text-sm md:text-base font-medium text-gray-500 text-right whitespace-pre-line">{metric.valueExtra}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Material Usage and Labor Costs Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5 sm:gap-2 md:gap-6">
                {/* Material Usage Section */}
                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-2 sm:p-3 md:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2 md:mb-4">
                    <h2 className="text-xs sm:text-sm md:text-lg font-semibold text-gray-900">Material Usage</h2>
                    <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-900">
                      ${materialUsageData.reduce((sum, category) => sum + category.total_cost, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>

                  <div className="space-y-1 sm:space-y-2 max-h-[250px] sm:max-h-[300px] md:max-h-[400px] overflow-y-auto px-0.5">
                    {isLoadingMaterials ? (
                      <div className="flex items-center justify-center py-4 sm:py-6 md:py-8">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-t-2 border-gray-200 rounded-full animate-spin border-t-blue-600"></div>
                      </div>
                    ) : materialUsageData.length === 0 ? (
                      <div className="py-4 sm:py-6 md:py-8 text-center text-[10px] sm:text-xs md:text-sm text-gray-500">
                        No material usage data available
                      </div>
                    ) : (
                      <div className="space-y-1 sm:space-y-2">
                        {materialUsageData.map((category) => (
                          <div key={category.category} className="border border-gray-100 rounded-lg overflow-hidden">
                            <button
                              onClick={() => {
                                setMaterialUsageData(prev =>
                                  prev.map(cat =>
                                    cat.category === category.category
                                      ? { ...cat, isExpanded: !cat.isExpanded }
                                      : cat
                                  )
                                );
                              }}
                              className="w-full flex items-center justify-between p-2 sm:p-3 hover:bg-gray-50 transition-colors duration-150"
                            >
                              <div className="flex items-center space-x-1.5 sm:space-x-2">
                                <div className="bg-white rounded-md sm:rounded-lg p-1 border border-gray-200">
                                  <ChevronRight
                                    className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-500 transition-transform duration-200 ${
                                      category.isExpanded ? 'transform rotate-90' : ''
                                    }`}
                                  />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[11px] sm:text-sm font-semibold text-gray-900 truncate">{category.category}</p>
                                  <p className="text-[9px] sm:text-xs text-gray-500">
                                    {category.materials.length} {category.materials.length === 1 ? 'material' : 'materials'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right ml-2">
                                <p className="text-[11px] sm:text-sm font-semibold text-gray-900">
                                  ${category.total_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </p>
                              </div>
                            </button>

                            {category.isExpanded && (
                              <div className="border-t border-gray-100 bg-gray-50/50">
                                {category.materials.map((material) => (
                                  <div
                                    key={material.id}
                                    className="flex items-center justify-between p-2 sm:p-3 border-b border-gray-100 last:border-b-0 hover:bg-white transition-colors duration-150"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[11px] sm:text-sm text-gray-700 truncate">{material.name}</p>
                                      <p className="text-[9px] sm:text-xs text-gray-500">
                                        {material.quantity} {material.unit} @ ${material.cost_per_unit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/{material.unit}
                                      </p>
                                    </div>
                                    <div className="text-right ml-2">
                                      <p className="text-[11px] sm:text-sm font-medium text-gray-700">
                                        ${material.total_cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Labor Report Section */}
                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-2 sm:p-3 md:p-6">
                    <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                      <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">Labor Report</h2>
                      <div className="text-sm sm:text-base font-medium text-gray-900">
                        ${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th scope="col" className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                              Resource
                            </th>
                            <th scope="col" className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                              Earnings
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {laborData.map((resource) => (
                            <tr key={resource.resource_id} className="hover:bg-gray-50">
                              <td className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[10px] sm:text-xs text-gray-900 whitespace-nowrap">
                                {resource.first_name} {resource.last_name}
                              </td>
                              <td className="px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[10px] sm:text-xs text-gray-900 whitespace-nowrap text-right">
                                ${resource.total_earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Money In and Out - Full Width */}
              <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-2 sm:p-3 md:p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="p-1.5 sm:p-2 bg-blue-50 rounded-lg">
                      <BanknoteIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-blue-600" />
                    </div>
                    <h2 className="text-sm sm:text-base md:text-xl font-semibold text-gray-900">Money In and Out</h2>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="text-xs sm:text-sm text-gray-600 font-medium">Material Usage</span>
                      <button
                        type="button"
                        onClick={() => handleStockHistoryToggle(!useStockHistory)}
                        className={`
                          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20
                          ${useStockHistory ? 'bg-blue-600' : 'bg-gray-200'}
                        `}
                      >
                        <span
                          className={`
                            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 
                            transition duration-200 ease-in-out
                            ${useStockHistory ? 'translate-x-5' : 'translate-x-0'}
                          `}
                        />
                      </button>
                      <span className="text-xs sm:text-sm text-gray-600 font-medium">Stock History</span>
                    </div>
                    <div className={`flex items-center ${paymentData.paymentsInChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {paymentData.paymentsInChange >= 0 ? (
                        <ArrowUpCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                      ) : (
                        <ArrowDownCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                      <span className="text-xs sm:text-sm font-medium ml-1">
                        {paymentData.paymentsInChange >= 0 ? '+' : ''}{paymentData.paymentsInChange}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Net Profit */}
                  <div className="p-4 sm:p-5 bg-gray-50/80 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <h3 className="text-sm sm:text-base font-medium text-gray-700">Net Profit</h3>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-xl sm:text-2xl md:text-3xl font-bold ${
                            paymentData.totalPaymentsIn - (
                              laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                              (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                              fixedExpenses.totalFixedCost
                            ) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            ${Math.abs(paymentData.totalPaymentsIn - (
                              laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                              (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                              fixedExpenses.totalFixedCost
                            )).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-sm sm:text-base text-gray-500 font-medium">
                            {((paymentData.totalPaymentsIn - (
                              laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) + 
                              (useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) + 
                              fixedExpenses.totalFixedCost
                            )) / (paymentData.totalPaymentsIn || 1) * 100).toFixed(1)}% margin
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Money Flow Breakdown */}
                  <div className="space-y-5">
                    {/* Money In */}
                    <div className="space-y-2.5">
                      <div className="flex justify-between items-baseline">
                        <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                          ${paymentData.totalPaymentsIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-sm sm:text-base text-gray-600 font-medium">Money In</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                          style={{ width: `${(paymentData.totalPaymentsIn / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="space-y-5 pt-2">
                      {/* Labor Costs */}
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                            ${laborData.reduce((sum, resource) => sum + resource.total_earnings, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-sm sm:text-base text-gray-600 font-medium">Labor Costs</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                          <div 
                            className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                            style={{ width: `${(laborData.reduce((sum, resource) => sum + resource.total_earnings, 0) / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Supplies Costs */}
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                            ${(useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-sm sm:text-base text-gray-600 font-medium">
                            Supplies Costs {useStockHistory ? '(Stock)' : '(Materials)'}
                          </span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                          <div 
                            className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                            style={{ width: `${((useStockHistory ? stockData.totalSuppliesCost : materialUsageData.reduce((sum, category) => sum + category.total_cost, 0)) / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Fixed Expenses */}
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                            ${fixedExpenses.totalFixedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="text-sm sm:text-base text-gray-600 font-medium">Fixed Expenses</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                          <div 
                            className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full shadow-sm transform origin-left transition-transform duration-1000 ease-out"
                            style={{ width: `${(fixedExpenses.totalFixedCost / (paymentData.totalPaymentsIn || 1)) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated Profit Report */}
              {/* Profit by Job */}
              <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-1 sm:p-3 md:p-6">
                  <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-1 sm:mb-3 md:mb-4">Profit by Job</h2>
                  <div className="overflow-x-auto -mx-1 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <div className="max-h-[300px] overflow-y-auto border-t border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Job #
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Date
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50 hidden sm:table-cell">
                                Title
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-right text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Revenue
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-right text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Costs
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-right text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Profit
                              </th>
                              <th scope="col" className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-right text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50">
                                Margin
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {profitByJobData
                              .filter((booking) => booking.completed_at && isDateInRange(booking.completed_at, selectedDateRange.id, customStartDate, customEndDate))
                              .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
                              .map((booking: ProfitByJobBooking) => {
                                const revenue = booking.subtotal - (booking.discount_amount || 0);
                                const costs = (booking.material_costs || 0) + (booking.labour_costs || 0);
                                const profit = revenue - costs;
                                const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

                                return (
                                  <tr key={booking.id} className="hover:bg-gray-50">
                                    <td className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs text-gray-900 whitespace-nowrap">
                                      {booking.booking_number || booking.id}
                                    </td>
                                    <td className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs text-gray-500 whitespace-nowrap">
                                      {booking.completed_at ? formatDate(new Date(booking.completed_at)) : ''}
                                    </td>
                                    <td className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs text-gray-900 hidden sm:table-cell">
                                      {booking.title}
                                    </td>
                                    <td className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs text-gray-900 whitespace-nowrap text-right">
                                      ${revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs text-red-600 whitespace-nowrap text-right">
                                      -${costs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={`px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs whitespace-nowrap text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      ${profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={`px-0.5 py-0.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-[8px] sm:text-xs whitespace-nowrap text-right ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {margin.toFixed(1)}%
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
